import { useCallback, useEffect, useState } from 'react';
import {
  isAccessTokenValid,
  createSecureVault,
  unlockPrf,
  refreshAccessTokenWith,
  registerUnlockPrompt,
  setCachedRefreshToken,
  setPendingRefreshToken,
} from '../dry/secure';
import { config } from '../dry/config';
import { http } from '../dry/client';

export type GateState =
  | 'checking'
  | 'authed'        // A: valid access token (or unlocked this session) → render app
  | 'locked-online' // B: unlock → refresh → app
  | 'locked-offline'// C: unlock → app (degraded, defer refresh)
  | 'login';        // D: not enrolled → /login

/**
 * Thrown by the unlock actions when the vault decrypted successfully (correct
 * PIN/biometric) but the subsequent online `/auth/refresh` was rejected by the
 * server (e.g. the 30-day refresh token has itself expired). The LockScreen
 * reads this to show "session expired — sign in again" rather than the
 * misleading "Incorrect PIN", and to point the user at the escape hatch.
 */
export const REFRESH_FAILED = 'REFRESH_FAILED';

/**
 * Classify a failed `/auth/refresh` by HTTP status so we can tell a genuinely
 * dead refresh token from a mere transport failure.
 *
 * DryConnect's `ConnectError.status` is `number | undefined`: undefined for a
 * network error / timeout (no response was received), the real code for an HTTP
 * rejection. So:
 *   - 401 / 403 → the server actively rejected the 30-day refresh token: it is
 *     genuinely dead → 'expired' → LockScreen shows "session expired" + escape.
 *   - anything else (undefined network error, timeout, 5xx, captive portal) →
 *     'transport': the backend was unreachable, NOT an auth problem → degrade
 *     into offline mode and let the 401 interceptor retry refresh later.
 */
export function classifyRefreshError(err: unknown): 'expired' | 'transport' {
  const e = err as { status?: number; response?: { status?: number } };
  const s = e?.status ?? e?.response?.status;
  return s === 401 || s === 403 ? 'expired' : 'transport';
}

/**
 * In-memory, session-scoped "the user proved they own this device this session"
 * flag. Set TRUE on ANY successful unlock — online (refresh succeeded) or
 * offline (degraded). It lets `computeGateState` return 'authed' even though the
 * plaintext access token is still expired, which is the whole point of offline
 * unlock: the app must render so capture can continue. It is module-level and
 * therefore resets on a full reload — each cold start re-prompts unlock, which
 * is intended. NEVER persisted.
 */
let unlocked = false;

/**
 * Reset the in-memory session unlock flag. Called on logout so a soft (no-reload)
 * logout does NOT leave `unlocked` TRUE for the next user signing in in the same
 * tab — pairing with `setCachedRefreshToken(null)` to prevent cross-user
 * refresh-token reuse. Also serves as the test seam.
 */
export function resetUnlockSession() {
  unlocked = false;
}

/** Test seam alias — reset the session unlock flag. */
export const __resetUnlockedForTest = resetUnlockSession;

export interface GateInputs {
  tokenValid: boolean;
  unlocked: boolean;
  enrolled: boolean;
  online: boolean;
}

/**
 * Pure gate-state precedence. Extracted so the highest-value behaviour (the
 * `unlocked` → 'authed' degraded transition) is unit-testable without the DOM,
 * localStorage, or the vault. Precedence:
 *   1. valid access token            → 'authed'
 *   2. unlocked this session         → 'authed' (degraded; reads from cache,
 *                                       writes to the local capture collection;
 *                                       a later 401 drives the online refresh)
 *   3. not enrolled                  → 'login'
 *   4. enrolled, online vs offline   → 'locked-online' | 'locked-offline'
 */
export function computeGateState({ tokenValid, unlocked, enrolled, online }: GateInputs): GateState {
  if (tokenValid) return 'authed';
  if (unlocked) return 'authed';
  if (!enrolled) return 'login';
  return online ? 'locked-online' : 'locked-offline';
}

/** Read live inputs and evaluate which gate state applies right now. */
function computeState(): GateState {
  const token = localStorage.getItem(config.auth.storageKey);
  const vault = createSecureVault();
  return computeGateState({
    tokenValid: isAccessTokenValid(token),
    unlocked,
    enrolled: vault.isEnrolled(),
    online: navigator.onLine,
  });
}

export function useOfflineUnlock() {
  // Compute synchronously on first render so a valid-token user never sees a
  // blank "checking" flash before the effect runs.
  const [state, setState] = useState<GateState>(() => computeState());

  const evaluate = useCallback(() => {
    setState(computeState());
  }, []);

  useEffect(() => {
    evaluate();
    const onOnline = () => evaluate();
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOnline);
    };
  }, [evaluate]);

  // Unlock with biometrics: decrypt refresh token; if online, refresh access token.
  const unlockBiometric = useCallback(async () => {
    const vault = createSecureVault();
    const prfOutput = await unlockPrf(); // throws → LockScreen shows "Unlock failed"
    const refreshToken = await vault.unlockWithPrfOutput(prfOutput);
    setCachedRefreshToken(refreshToken);
    if (navigator.onLine) {
      // Vault unlock already succeeded; only the refresh call may fail. Classify
      // the failure: a 401/403 means the 30-day refresh token is itself dead, so
      // we rethrow REFRESH_FAILED and do NOT set `unlocked` (user stays on the
      // LockScreen with the escape hatch). Any other failure (network error /
      // timeout / captive portal / 5xx) is a TRANSPORT problem, not auth — fall
      // through to set `unlocked` and degrade into offline mode; the 401
      // interceptor retries refresh once the backend is actually reachable.
      try {
        await refreshAccessTokenWith(async () => refreshToken, (u, b) =>
          http.request({ url: u, method: 'POST', body: b }),
        );
      } catch (err) {
        if (classifyRefreshError(err) === 'expired') throw new Error(REFRESH_FAILED);
        console.warn('[offline-unlock] refresh deferred (transport failure) — degrading to offline');
      }
    }
    // Reached on: offline unlock, a successful online refresh, or a transport
    // failure we deliberately degraded past.
    unlocked = true;
    evaluate();
  }, [evaluate]);

  const unlockPin = useCallback(async (pin: string) => {
    const vault = createSecureVault();
    const refreshToken = await vault.unlockWithPin(pin); // throws / wipes on wrong PIN
    setCachedRefreshToken(refreshToken);
    if (navigator.onLine) {
      // Same expired-vs-transport split as unlockBiometric: only a 401/403 from
      // the refresh endpoint means the refresh token is dead; everything else
      // degrades to offline so a captive portal / dead backend can't masquerade
      // as "session expired".
      try {
        await refreshAccessTokenWith(async () => refreshToken, (u, b) =>
          http.request({ url: u, method: 'POST', body: b }),
        );
      } catch (err) {
        if (classifyRefreshError(err) === 'expired') throw new Error(REFRESH_FAILED);
        console.warn('[offline-unlock] refresh deferred (transport failure) — degrading to offline');
      }
    }
    unlocked = true;
    evaluate();
  }, [evaluate]);

  // Escape hatch from the LockScreen: abandon the stale offline session and fall
  // back to a fresh password sign-in. Works OFFLINE (no network call): it only
  // clears local session state. Captures are untouched — they live in the
  // DryStore formSubmissions collection, not the vault.
  //
  // Deliberately does NOT wipe the vault. This button is shown even OFFLINE
  // (pre-unlock), where a wipe is irreversible and re-enrollment is impossible
  // (it needs a fresh online login) — so wiping here would brick a field
  // worker's unlock. A stale/dead refresh token left in the vault is harmless:
  // the next online refresh just 401s → the user lands here again, and they can
  // disable/re-enroll from /profile after re-login. The deliberate-logout case
  // (DryContext.logout) still wipes the vault, which is the correct place for it.
  //
  // We hard-navigate to /login rather than relying on evaluate() → 'login',
  // because isAuthenticated is derived from DryContext's in-memory `user`, which
  // a localStorage clear does NOT reset; a full navigation re-inits the provider
  // from the (now cleared) storage so the user is genuinely unauthenticated.
  const signInAgain = useCallback(() => {
    try { localStorage.removeItem(config.auth.storageKey); } catch { /* ignore */ }
    try { localStorage.removeItem('user'); } catch { /* ignore */ }
    try { localStorage.removeItem('tenant'); } catch { /* ignore */ }
    try { localStorage.removeItem('auth-storage'); } catch { /* ignore */ }
    setCachedRefreshToken(null);
    setPendingRefreshToken(null);
    unlocked = false;
    // Hard navigate (not just evaluate()): isAuthenticated is derived from
    // DryContext's in-memory `user`, which a localStorage clear does not reset.
    // A full navigation re-inits the provider from the cleared storage so the
    // user is genuinely unauthenticated and lands on /login. We skip a local
    // evaluate() here to avoid flashing protected content for one frame before
    // the reload. In a non-browser (test) environment, fall back to evaluate().
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    } else {
      evaluate();
    }
  }, [evaluate]);

  // Let the HTTP 401 interceptor drive an unlock when it needs the refresh token.
  // The visible LockScreen (driven by `state`) is the real unlock path; here we
  // deliberately reject so the interceptor defers to it rather than spawning a
  // modal-from-interceptor. After a visible unlock, setCachedRefreshToken means
  // most subsequent 401s resolve from cache without re-prompting at all.
  useEffect(() => {
    registerUnlockPrompt(async () => {
      throw new Error('Unlock required');
    });
    return () => registerUnlockPrompt(null);
  }, []);

  return { state, unlockBiometric, unlockPin, signInAgain, refresh: evaluate };
}
